import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useBackdropClose from "../../hooks/useBackdropClose";
import CatalogItem from "../catalogItem";
import { formatAgeRange } from "../catalogItem/format";
import {
  Overlay, Modal, Header, ModalTitle, SearchWrap, SearchInput,
  FilterRow, FilterLabel, FilterField, FilterFieldLabel, FilterSelect,
  OptionsList, OptionRow, HiddenInput, Box, Status, Footer, CancelButton, ConfirmButton,
} from "./styles";

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ALL = "";

// Busqueda sin mayusculas ni acentos: "semioscurato" encuentra "Semioscurato".
const normalize = (text) =>
  (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const matches = (query, ...fields) => {
  const q = normalize(query.trim());
  return !q || fields.some((field) => normalize(field).includes(q));
};

/**
 * Esqueleto comun de los tres selectores. Se abre encima de la ficha de la
 * actividad, por eso va en su propio portal y con un z-index mayor.
 */
const PickerShell = ({
  title, wide, search, onSearch, searchPlaceholder, filters,
  status, confirmLabel, onCancel, onConfirm, children,
}) => {
  const { t } = useTranslation();
  const backdrop = useBackdropClose(onCancel);

  return createPortal(
    <Overlay {...backdrop}>
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="catalog-picker-title"
        $wide={wide}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <ModalTitle id="catalog-picker-title">{title}</ModalTitle>
          {onSearch && (
            <SearchWrap>
              <SearchIcon />
              <SearchInput
                type="search"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            </SearchWrap>
          )}
        </Header>

        {filters && (
          <FilterRow>
            <FilterLabel>{t("activityPlan.filterBy")}</FilterLabel>
            {filters}
          </FilterRow>
        )}

        {status === "loading" && <Status>{t("common.loading")}</Status>}
        {status === "error" && <Status>{t("activityCard.catalogError")}</Status>}
        {status === "ready" && children}

        <Footer>
          <CancelButton type="button" onClick={onCancel}>{t("common.cancel")}</CancelButton>
          <ConfirmButton type="button" onClick={onConfirm} disabled={status !== "ready"}>
            {confirmLabel}
          </ConfirmButton>
        </Footer>
      </Modal>
    </Overlay>,
    document.body
  );
};

/** Casilla (o radio) + elemento: todo el bloque es la etiqueta del input. */
const Option = ({ item, kind, type, name, checked, onChange }) => (
  <OptionRow>
    <HiddenInput type={type} name={name} checked={checked} onChange={onChange} />
    <Box $checked={checked} $round={type === "radio"} aria-hidden="true">
      {checked && type === "checkbox" && <CheckIcon />}
    </Box>
    <CatalogItem item={item} kind={kind} selected={checked} />
  </OptionRow>
);

const Select = ({ label, value, onChange, disabled, children }) => (
  <FilterField>
    <FilterFieldLabel>{label}</FilterFieldLabel>
    <FilterSelect value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {children}
    </FilterSelect>
  </FilterField>
);

/* ── Context: uno solo ── */
export const ContextPickerModal = ({ catalog, status, selectedId, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(selectedId || null);
  const contexts = catalog?.contexts || [];

  return (
    <PickerShell
      title={t("activityCard.contextSelection")}
      status={status}
      confirmLabel={t("activityCard.selectContext")}
      onCancel={onCancel}
      onConfirm={() => onConfirm(contexts.find((c) => c.id === selected) || null)}
    >
      <OptionsList role="radiogroup">
        {contexts.map((item) => (
          <Option
            key={item.id}
            item={item}
            kind="contexts"
            type="radio"
            name="context"
            checked={selected === item.id}
            onChange={() => setSelected(item.id)}
          />
        ))}
      </OptionsList>
    </PickerShell>
  );
};

/**
 * Seleccion multiple comun a materials y postures. Al confirmar, los que ya
 * estaban conservan su orden en la ficha y los nuevos se anaden al final, en el
 * orden del catalogo.
 */
const useMultiSelect = (items, selectedIds) => {
  const [selected, setSelected] = useState(() => new Set(selectedIds));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const result = () => {
    const byId = new Map(items.map((item) => [item.id, item]));
    const kept = selectedIds.filter((id) => selected.has(id));
    const added = items.map((item) => item.id).filter((id) => selected.has(id) && !selectedIds.includes(id));
    return [...kept, ...added].map((id) => byId.get(id)).filter(Boolean);
  };

  return { selected, toggle, result };
};

/* ── Materials: objects y supports ── */
export const MaterialsPickerModal = ({ catalog, status, selectedIds, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const materials = useMemo(() => catalog?.materials || [], [catalog]);
  const { selected, toggle, result } = useMultiSelect(materials, selectedIds);

  const [search, setSearch]         = useState("");
  const [type, setType]             = useState(ALL);
  const [categoryId, setCategoryId] = useState(ALL);
  const [ageKey, setAgeKey]         = useState(ALL);

  // Las opciones salen del propio catalogo: categorias de los soportes y
  // franjas de edad de los objetos.
  const categories = useMemo(() => {
    const seen = new Map();
    materials
      .filter((m) => m.type === "support" && m.category_id != null)
      .forEach((m) => seen.set(m.category_id, m.category));
    return [...seen].sort((a, b) => a[0] - b[0]);
  }, [materials]);

  const ageRanges = useMemo(() => {
    const seen = new Map();
    materials
      .filter((m) => m.type === "object" && (m.age_min_months != null || m.age_max_months != null))
      .forEach((m) => seen.set(`${m.age_min_months}-${m.age_max_months}`, m));
    return [...seen]
      .sort((a, b) => (a[1].age_min_months ?? 0) - (b[1].age_min_months ?? 0))
      .map(([key, m]) => [key, formatAgeRange(t, m.age_min_months, m.age_max_months)]);
  }, [materials, t]);

  const changeType = (value) => {
    setType(value);
    // Cada uno de estos filtros solo tiene sentido con su tipo.
    setCategoryId(ALL);
    setAgeKey(ALL);
  };

  const visible = materials.filter((m) => {
    if (type !== ALL && m.type !== type) return false;
    if (categoryId !== ALL && String(m.category_id) !== categoryId) return false;
    if (ageKey !== ALL && `${m.age_min_months}-${m.age_max_months}` !== ageKey) return false;
    return matches(search, m.label);
  });

  return (
    <PickerShell
      wide
      title={t("activityCard.materialsSelection")}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t("activityCard.searchObject")}
      status={status}
      confirmLabel={t("activityCard.selectMaterials")}
      onCancel={onCancel}
      onConfirm={() => onConfirm(result())}
      filters={(
        <>
          <Select label={t("activityCard.genericType")} value={type} onChange={changeType}>
            <option value={ALL}>{t("activityPlan.all")}</option>
            <option value="object">{t("activityCard.object")}</option>
            <option value="support">{t("activityCard.support")}</option>
          </Select>
          <Select
            label={t("activityCard.specificType")}
            value={categoryId}
            onChange={setCategoryId}
            disabled={type !== "support"}
          >
            <option value={ALL}>{type === "support" ? t("activityPlan.all") : "-"}</option>
            {categories.map(([id, label]) => (
              <option key={id} value={String(id)}>{label}</option>
            ))}
          </Select>
          <Select
            label={t("activityCard.targetAge")}
            value={ageKey}
            onChange={setAgeKey}
            disabled={type !== "object"}
          >
            <option value={ALL}>{type === "object" ? t("activityPlan.all") : "-"}</option>
            {ageRanges.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
        </>
      )}
    >
      {visible.length ? (
        <OptionsList $columns={2}>
          {visible.map((item) => (
            <Option
              key={item.id}
              item={item}
              kind="materials"
              type="checkbox"
              checked={selected.has(item.id)}
              onChange={() => toggle(item.id)}
            />
          ))}
        </OptionsList>
      ) : (
        <Status>{t("activityCard.noResults")}</Status>
      )}
    </PickerShell>
  );
};

/* ── Postures ── */
export const PosturesPickerModal = ({ catalog, status, selectedIds, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const postures = useMemo(() => catalog?.postures || [], [catalog]);
  const { selected, toggle, result } = useMultiSelect(postures, selectedIds);

  const [search, setSearch]   = useState("");
  const [groupId, setGroupId] = useState(ALL);

  // El catalogo ya llega ordenado por grupo, asi que el orden de aparicion es
  // el de las posturas genericas.
  const groups = useMemo(() => {
    const seen = new Map();
    postures.forEach((p) => {
      if (!seen.has(p.group_id)) seen.set(p.group_id, p.posture_group);
    });
    return [...seen];
  }, [postures]);

  const visible = postures.filter((p) => {
    if (groupId !== ALL && String(p.group_id) !== groupId) return false;
    return matches(search, p.label, p.description);
  });

  return (
    <PickerShell
      wide
      title={t("activityCard.posturesSelection")}
      search={search}
      onSearch={setSearch}
      searchPlaceholder={t("activityCard.searchPosture")}
      status={status}
      confirmLabel={t("activityCard.selectPostures")}
      onCancel={onCancel}
      onConfirm={() => onConfirm(result())}
      filters={(
        <Select label={t("activityCard.genericPosture")} value={groupId} onChange={setGroupId}>
          <option value={ALL}>{t("activityPlan.all")}</option>
          {groups.map(([id, label]) => (
            <option key={id} value={String(id)}>{label}</option>
          ))}
        </Select>
      )}
    >
      {visible.length ? (
        <OptionsList $columns={2}>
          {visible.map((item) => (
            <Option
              key={item.id}
              item={item}
              kind="postures"
              type="checkbox"
              checked={selected.has(item.id)}
              onChange={() => toggle(item.id)}
            />
          ))}
        </OptionsList>
      ) : (
        <Status>{t("activityCard.noResults")}</Status>
      )}
    </PickerShell>
  );
};
